/**
 * Phase 5d — Composite acknowledgment-state pill.
 *
 * States: none (report not yet finalized) | not_yet | acknowledged | stale
 * `acknowledged` rows may also carry a +stale chip when stale_ack_count > 0.
 */

const PILL_BASE =
  "inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide";

export default function AcknowledgmentStateChip({ row, compact = false }) {
  const status = row.status;
  if (status !== "finalized") {
    return <span className="text-slate-400 text-xs">—</span>;
  }
  const active = row.active_ack_count || 0;
  const stale = row.stale_ack_count || 0;

  if (active > 0) {
    return (
      <div className="flex flex-col gap-1" data-testid="ack-chip-acknowledged">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={`${PILL_BASE} bg-emerald-100 text-emerald-900 border-emerald-300`}
          >
            Acknowledged
            {active > 1 ? ` ×${active}` : ""}
          </span>
          {stale > 0 && (
            <span
              title="Acknowledged on a prior finalized version of this report."
              className={`${PILL_BASE} bg-amber-100 text-amber-900 border-amber-300`}
            >
              +stale
            </span>
          )}
        </div>
        {!compact && row.latest_active_ack_name && (
          <span className="text-[11px] text-slate-600 truncate">
            by {row.latest_active_ack_name}
            {row.latest_active_ack_at
              ? ` · ${_relative(row.latest_active_ack_at)}`
              : ""}
          </span>
        )}
      </div>
    );
  }

  if (stale > 0) {
    return (
      <span
        data-testid="ack-chip-stale"
        title="Acknowledged on a prior finalized version of this report."
        className={`${PILL_BASE} bg-amber-100 text-amber-900 border-amber-300`}
      >
        Stale
      </span>
    );
  }

  return (
    <span
      data-testid="ack-chip-not-yet"
      className={`${PILL_BASE} bg-slate-100 text-slate-700 border-slate-300`}
    >
      Not yet
    </span>
  );
}

function _relative(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const ms = Date.now() - dt.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
